import type { PropsWithChildren, RefObject } from 'react';
import { useImperativeHandle, useRef } from 'react';

import type { Spacing } from '#/theme/theme';
import { spacing } from '#/theme/theme';

import { ScrollAreaHolder, ScrollAreaViewport } from './ScrollArea.styled';

export type ScrollAreaHandle = {
  holderElement: HTMLDivElement | null;
  viewportElement: HTMLDivElement | null;
};

type Props = {
  direction: 'vertical' | 'horizontal';
  height?: string;
  hideScrollBar?: boolean;
  qaId?: string;
  width?: string;
  scrollShadowSize?: Spacing;
  showScrollShadowEnd?: boolean;
  showScrollShadowStart?: boolean;
  ref?: RefObject<ScrollAreaHandle | null>;
};

export function ScrollArea({
  children,
  direction,
  height = 'inherit',
  hideScrollBar = false,
  qaId,
  width = 'inherit',
  scrollShadowSize = spacing[30],
  showScrollShadowEnd,
  showScrollShadowStart,
  ref,
}: PropsWithChildren<Props>) {
  const holderRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(ref, () => ({
    holderElement: holderRef.current,
    viewportElement: viewportRef.current,
  }));

  return (
    <ScrollAreaHolder ref={holderRef} $direction={direction}>
      <ScrollAreaViewport
        ref={viewportRef}
        data-qa-id={qaId}
        $direction={direction}
        $height={height}
        $hideScrollBar={hideScrollBar}
        $width={width}
        $scrollShadowSize={scrollShadowSize}
        $showScrollShadowStart={showScrollShadowStart}
        $showScrollShadowEnd={showScrollShadowEnd}
      >
        {children}
      </ScrollAreaViewport>
    </ScrollAreaHolder>
  );
}
